import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  User,
  Calendar as CalIcon,
  Ban,
  Edit3,
  Save,
  ChevronDown,
  MessageSquare,
  Gift,
  Copy,
  Users,
  Share2,
} from "lucide-react";
import {
  customerApi,
  getCustomerToken,
  setCustomerToken,
  clearCustomerToken,
} from "@/lib/customerAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

// ────────────────────────── LOGIN / SET-PW / RESET-PW ──────────────────────────

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center px-6">
    <div className="w-full max-w-md">
      <Link
        to="/"
        className="block text-center text-xs uppercase tracking-[0.32em] text-[#B76E79] font-brand mb-8"
      >
        Walnut Lakes
      </Link>
      <div className="bg-[#141212] border border-[#2a1a1c] rounded-2xl p-8 md:p-10 shadow-xl">
        <h1 className="font-serif text-2xl md:text-3xl text-[#F5EFE6]">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-[#F5EFE6]/70">{subtitle}</p>}
        {children}
      </div>
    </div>
  </div>
);

export const CustomerLogin = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getCustomerToken()) nav("/account", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await customerApi.post(`/login`, { email, password });
      setCustomerToken(data.token);
      toast.success("Welcome back.");
      nav("/account", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Manage your Walnut Lakes bookings.">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-login-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-login-email"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-login-password"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full h-11 rounded-lg font-brand tracking-[0.18em] text-xs uppercase bg-gradient-to-r from-[#B76E79] to-[#8C4E58] hover:from-[#C48690] hover:to-[#A75E68] text-[#F5EFE6] transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="customer-login-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Sign in"}
        </button>
      </form>
      <div className="mt-6 flex justify-between text-xs text-[#F5EFE6]/70">
        <Link to="/account/forgot-password" className="hover:text-[#F5EFE6]">
          Forgot password?
        </Link>
        <Link to="/" className="hover:text-[#F5EFE6]">
          Back to site
        </Link>
      </div>
      <div className="mt-4 pt-4 border-t border-[#2a1a1c] text-center text-xs text-[#F5EFE6]/70">
        New here?{" "}
        <Link to="/account/register" data-testid="customer-login-to-register" className="text-[#E4B2A8] hover:text-[#F5EFE6] font-medium">
          Create an account
        </Link>
      </div>
    </AuthShell>
  );
};

export const CustomerRegister = () => {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getCustomerToken()) nav("/account", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await customerApi.post(`/register`, { name, email, phone, password });
      setCustomerToken(data.token);
      toast.success("Account created. Welcome to Walnut Lakes.");
      nav("/account", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Manage bookings, unlock loyalty rewards and refer friends.">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-register-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Full name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={1}
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-register-name"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-register-email"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Phone</Label>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            minLength={6}
            placeholder="07XXX XXX XXX"
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-register-phone"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Password (min 8 characters)</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-register-password"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Confirm password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-register-confirm"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full h-11 rounded-lg font-brand tracking-[0.18em] text-xs uppercase bg-gradient-to-r from-[#B76E79] to-[#8C4E58] hover:from-[#C48690] hover:to-[#A75E68] text-[#F5EFE6] transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="customer-register-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Create account"}
        </button>
      </form>
      <div className="mt-6 text-center text-xs text-[#F5EFE6]/70">
        Already have an account?{" "}
        <Link to="/account/login" data-testid="customer-register-to-login" className="text-[#E4B2A8] hover:text-[#F5EFE6] font-medium">
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
};

export const CustomerSetPassword = () => {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const token = search.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const isReset = window.location.pathname.includes("reset-password");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isReset ? `/reset-password` : `/set-password`;
      const { data } = await customerApi.post(endpoint, { token, password });
      setCustomerToken(data.token);
      toast.success("Password set. Signed in.");
      nav("/account", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not set password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link">
        <p className="mt-4 text-sm text-[#F5EFE6]/80">
          This link is missing its token. Please open the link from your email again, or{" "}
          <Link to="/account/forgot-password" className="underline">
            request a new one
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={isReset ? "Reset password" : "Set your password"} subtitle="Choose something at least 8 characters.">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-setpw-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">New password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-setpw-password"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Confirm password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
            data-testid="customer-setpw-confirm"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full h-11 rounded-lg font-brand tracking-[0.18em] text-xs uppercase bg-gradient-to-r from-[#B76E79] to-[#8C4E58] text-[#F5EFE6] transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="customer-setpw-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Save password"}
        </button>
      </form>
    </AuthShell>
  );
};

export const CustomerForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await customerApi.post(`/forgot-password`, { email });
      setSent(true);
    } catch {
      setSent(true); // don't reveal whether the address exists
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Forgot password" subtitle={sent ? "" : "We'll email you a reset link."}>
      {sent ? (
        <p className="mt-4 text-sm text-[#F5EFE6]/80" data-testid="customer-forgot-sent">
          If an account exists for that email, a reset link is on its way. Check your inbox
          (and the spam folder) — links expire after 2 hours.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-forgot-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6]"
              data-testid="customer-forgot-email"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-lg font-brand tracking-[0.18em] text-xs uppercase bg-gradient-to-r from-[#B76E79] to-[#8C4E58] text-[#F5EFE6] transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="customer-forgot-submit"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Send reset link"}
          </button>
        </form>
      )}
      <div className="mt-6 text-xs text-[#F5EFE6]/70">
        <Link to="/account/login" className="hover:text-[#F5EFE6]">
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
};

// ────────────────────────── DASHBOARD ──────────────────────────

const StatusPill = ({ status }) => {
  const map = {
    paid: "bg-[#3B8C4E]/20 text-[#3B8C4E] border-[#3B8C4E]/40",
    pending: "bg-[#C89B5F]/20 text-[#8C6B2F] border-[#C89B5F]/40",
    cancelled: "bg-[#8C2E39]/20 text-[#8C2E39] border-[#8C2E39]/40",
    expired: "bg-[#6A5F58]/20 text-[#6A5F58] border-[#6A5F58]/40",
  };
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-medium border ${
        map[status] || "bg-[#DDD1BE] text-[#3A322E]"
      }`}
    >
      {status || "—"}
    </span>
  );
};

const BookingCard = ({ b, onCancel, onSave }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    start_date: b.start_date,
    end_date: b.end_date,
    guests: b.guests || 2,
    dogs: b.dogs || 0,
  });
  const dirty =
    form.start_date !== b.start_date ||
    form.end_date !== b.end_date ||
    Number(form.guests) !== (b.guests || 2) ||
    Number(form.dogs) !== (b.dogs || 0);

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8" data-testid={`booking-${b.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#B76E79] font-brand">
            {b.unit_type}
          </div>
          <h3 className="font-serif text-xl md:text-2xl text-[#0F0E0E] mt-1">
            {b.unit_name || b.unit_id}
          </h3>
          <p className="mt-1 text-sm text-[#3A322E]">
            {b.start_date} → {b.end_date} · {b.nights} night{b.nights > 1 ? "s" : ""} · {b.guests}{" "}
            guest{b.guests > 1 ? "s" : ""}
            {b.dogs > 0 ? ` · ${b.dogs} dog${b.dogs > 1 ? "s" : ""}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={b.payment_status} />
          <div className="text-xs text-[#6A5F58]">
            Total <span className="font-medium text-[#0F0E0E]">{gbp(b.total)}</span>
          </div>
        </div>
      </div>

      {/* Countdown */}
      {b.stay_status === "upcoming" && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B76E79]/12 text-[#B76E79] text-xs font-medium">
          <CalIcon size={13} />
          {b.days_until_arrival === 0
            ? "Arriving today"
            : `Your stay begins in ${b.days_until_arrival} day${b.days_until_arrival > 1 ? "s" : ""}`}
        </div>
      )}
      {b.stay_status === "in_progress" && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#3B8C4E]/12 text-[#3B8C4E] text-xs font-medium">
          Currently on-site — enjoy your stay
        </div>
      )}
      {b.stay_status === "past" && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#DDD1BE] text-[#6A5F58] text-xs font-medium">
          Past stay
        </div>
      )}

      {/* Actions */}
      {b.payment_status === "paid" && b.stay_status !== "past" && (
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setOpen((v) => !v)}
            className="btn-secondary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5"
            data-testid={`booking-modify-toggle-${b.id}`}
          >
            <Edit3 size={13} /> Modify
            <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={() => onCancel(b)}
            className="btn-ghost text-xs font-brand tracking-[0.14em] text-[#8C2E39] hover:text-[#5D1F27] inline-flex items-center gap-1.5"
            data-testid={`booking-cancel-${b.id}`}
          >
            <Ban size={13} /> Cancel booking
          </button>
          <a
            href={`/account/bookings/${b.id}/receipt`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-xs font-brand tracking-[0.14em] text-[#6A5F58] hover:text-[#0F0E0E]"
            data-testid={`booking-receipt-${b.id}`}
          >
            Receipt
          </a>
        </div>
      )}

      {/* Modify panel */}
      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 bg-[#F5EFE6] border border-[#DDD1BE] rounded-xl p-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Check-in</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="mt-1 bg-white h-10"
              data-testid={`booking-modify-start-${b.id}`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Check-out</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="mt-1 bg-white h-10"
              data-testid={`booking-modify-end-${b.id}`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Guests</Label>
            <Input
              type="number"
              min={1}
              max={4}
              value={form.guests}
              onChange={(e) => setForm({ ...form, guests: Number(e.target.value) })}
              className="mt-1 bg-white h-10"
              data-testid={`booking-modify-guests-${b.id}`}
            />
          </div>
          {b.unit_type === "lodge" && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Dogs</Label>
              <Input
                type="number"
                min={0}
                max={2}
                value={form.dogs}
                onChange={(e) => setForm({ ...form, dogs: Number(e.target.value) })}
                className="mt-1 bg-white h-10"
                data-testid={`booking-modify-dogs-${b.id}`}
              />
            </div>
          )}
          <div className="md:col-span-4 flex justify-end gap-2 mt-1">
            <button
              onClick={() => setOpen(false)}
              className="btn-ghost text-xs font-brand tracking-[0.14em]"
            >
              Close
            </button>
            <button
              disabled={!dirty}
              onClick={() => onSave(b, form)}
              className="btn-primary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid={`booking-modify-save-${b.id}`}
            >
              <Save size={13} /> Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ────────────────────────── REWARDS PANEL ──────────────────────────

const ProgressBar = ({ current, threshold }) => {
  const pct = Math.max(0, Math.min(100, threshold ? (current / threshold) * 100 : 0));
  return (
    <div className="w-full h-2 bg-[#F5EFE6] rounded-full overflow-hidden border border-[#DDD1BE]">
      <div
        className="h-full bg-gradient-to-r from-[#B76E79] to-[#E4B2A8] rounded-full transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const VoucherCard = ({ v }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(v.code);
      setCopied(true);
      toast.success("Voucher code copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy.");
    }
  };
  const expires = v.expires_at ? new Date(v.expires_at).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" }) : "—";
  const scopeLabel = v.unit_scope === "lodge"
    ? `Free night · Lodge (${v.unit_id || "same lodge"})`
    : "Free night · Any camping pitch";
  return (
    <div
      className="relative border border-dashed border-[#B76E79] bg-[#FBF7F1] rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4"
      data-testid={`voucher-${v.code}`}
    >
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#6A5F58]">{scopeLabel}</p>
        <p className="mt-2 font-mono text-2xl font-bold tracking-[0.10em] text-[#0F0E0E]">{v.code}</p>
        <p className="mt-1 text-xs text-[#6A5F58]">Valid until <strong>{expires}</strong> · Mon–Thu (no bank holidays)</p>
      </div>
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em]"
        data-testid={`copy-voucher-${v.code}`}
      >
        <Copy size={13} />
        {copied ? "Copied" : "Copy code"}
      </button>
    </div>
  );
};

const RewardsPanel = ({ rewards }) => {
  const touring = rewards?.touring || { threshold: 7, nights_completed: 0, nights_to_next_reward: 7, vouchers_earned: 0 };
  const lodges = rewards?.lodges || { threshold: 10, progress: [] };
  const vouchers = rewards?.vouchers || [];

  const hasAnyProgress =
    touring.nights_completed > 0 ||
    (lodges.progress || []).some((p) => p.nights_completed > 0);

  return (
    <section data-testid="rewards-panel">
      <p className="overline text-[#6A5F58]">
        <span className="marker-line" /> Rewards
      </p>
      <div className="flex items-center gap-3 mt-4">
        <Gift className="text-[#B76E79]" size={22} />
        <h2 className="font-serif text-2xl md:text-3xl text-[#14100F]">Free-night program</h2>
      </div>
      <p className="mt-2 text-sm text-[#6A5F58] max-w-2xl">
        Earn a complimentary night with every {touring.threshold} touring nights, or every {lodges.threshold} nights at the same lodge.
        Free nights are redeemable Monday to Thursday (excluding UK bank holidays) and expire 12 months after they&apos;re earned.
      </p>

      {vouchers.length > 0 && (
        <div className="mt-6 space-y-3">
          <p className="text-xs uppercase tracking-[0.18em] text-[#B76E79]">Your active vouchers</p>
          {vouchers.map((v) => <VoucherCard key={v.code} v={v} />)}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="rewards-touring-card">
          <div className="flex items-baseline justify-between">
            <p className="font-serif text-lg text-[#14100F]">Touring nights</p>
            <p className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">
              {touring.nights_completed} / {touring.threshold}
            </p>
          </div>
          <div className="mt-3">
            <ProgressBar current={touring.nights_completed % touring.threshold} threshold={touring.threshold} />
          </div>
          <p className="mt-3 text-sm text-[#3A322E]">
            {touring.nights_to_next_reward === 0
              ? "🎉 You've earned a free touring night — check your vouchers above."
              : `${touring.nights_to_next_reward} more night${touring.nights_to_next_reward === 1 ? "" : "s"} until your next free tourer night.`}
          </p>
          {touring.vouchers_earned > 0 && (
            <p className="mt-1 text-xs text-[#6A5F58]">Earned to date: {touring.vouchers_earned} voucher(s)</p>
          )}
        </div>

        <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="rewards-lodge-card">
          <div className="flex items-baseline justify-between">
            <p className="font-serif text-lg text-[#14100F]">Lodge nights (per lodge)</p>
            <p className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">Target {lodges.threshold}</p>
          </div>
          {(lodges.progress || []).length === 0 ? (
            <p className="mt-4 text-sm text-[#6A5F58]">
              Stay 10 nights at the same lodge (Heron / Kingfisher / Alpine) and your next night is on us.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {lodges.progress.map((p) => (
                <div key={p.unit_id}>
                  <div className="flex items-baseline justify-between">
                    <p className="text-sm text-[#3A322E] capitalize">{p.unit_id.replace("lodge-", "")}</p>
                    <p className="text-xs text-[#6A5F58]">{p.nights_completed} / {lodges.threshold}</p>
                  </div>
                  <div className="mt-1">
                    <ProgressBar
                      current={p.nights_completed % lodges.threshold}
                      threshold={lodges.threshold}
                    />
                  </div>
                  <p className="mt-1 text-xs text-[#6A5F58]">
                    {p.nights_to_next_reward === 0
                      ? "🎉 Free night earned — see vouchers above."
                      : `${p.nights_to_next_reward} more night${p.nights_to_next_reward === 1 ? "" : "s"} to your next free night.`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!hasAnyProgress && vouchers.length === 0 && (
        <p className="mt-6 text-sm text-[#6A5F58] italic">
          Once you&apos;ve completed a paid stay it&apos;ll appear here and your progress will start counting.
        </p>
      )}
    </section>
  );
};

// ────────────────────────── REFER-A-FRIEND PANEL ──────────────────────────

const ReferralPanel = ({ referral }) => {
  const [copied, setCopied] = useState(false);
  const code = referral?.code || "";
  const url = referral?.share_url || "";
  const discountLabel = referral?.friend_discount_label || "10%";
  const counts = referral?.counts || { total: 0, pending: 0, rewarded: 0 };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Referral link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy.");
    }
  };

  const shareText = encodeURIComponent(
    `Come and stay at Walnut Lakes — use my link and get ${discountLabel} off your first booking:`,
  );
  const shareUrlEnc = encodeURIComponent(url);
  const whatsapp = `https://wa.me/?text=${shareText}%20${shareUrlEnc}`;
  const emailShare = `mailto:?subject=${encodeURIComponent("Walnut Lakes — get 10% off your first stay")}&body=${shareText}%20${shareUrlEnc}`;

  if (!code) return null;

  return (
    <section data-testid="referral-panel">
      <p className="overline text-[#6A5F58]">
        <span className="marker-line" /> Refer a friend
      </p>
      <div className="flex items-center gap-3 mt-4">
        <Users className="text-[#B76E79]" size={22} />
        <h2 className="font-serif text-2xl md:text-3xl text-[#14100F]">Give {discountLabel}. Get a free night.</h2>
      </div>
      <p className="mt-2 text-sm text-[#6A5F58] max-w-2xl">
        Share your personal link with a friend. They get <strong>{discountLabel} off</strong> their first stay,
        and once their booking is complete you earn <strong>one bonus free-night voucher</strong>.
      </p>

      <div className="mt-6 rounded-2xl border border-dashed border-[#B76E79] bg-[#FBF7F1] p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#6A5F58]">Your link</p>
            <p
              className="mt-1 font-mono text-sm md:text-base text-[#0F0E0E] break-all"
              data-testid="referral-share-url"
            >
              {url}
            </p>
            <p className="mt-2 text-xs text-[#6A5F58]">
              Or share your code directly: <span className="font-mono font-bold tracking-[0.10em]">{code}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyUrl}
              data-testid="referral-copy"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em]"
            >
              <Copy size={13} /> {copied ? "Copied" : "Copy link"}
            </button>
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener"
              data-testid="referral-whatsapp"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors text-xs uppercase tracking-[0.14em]"
            >
              <Share2 size={13} /> WhatsApp
            </a>
            <a
              href={emailShare}
              data-testid="referral-email"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors text-xs uppercase tracking-[0.14em]"
            >
              <MessageSquare size={13} /> Email
            </a>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 text-center">
          <p className="text-3xl font-serif text-[#0F0E0E]">{counts.total}</p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] mt-1">Friends referred</p>
        </div>
        <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 text-center">
          <p className="text-3xl font-serif text-[#B76E79]">{counts.pending}</p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] mt-1">Pending completion</p>
        </div>
        <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 text-center">
          <p className="text-3xl font-serif text-[#0F0E0E]">{counts.rewarded}</p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] mt-1">Bonus nights earned</p>
        </div>
      </div>
    </section>
  );
};

export const CustomerAccount = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileEdit, setProfileEdit] = useState(false);
  const [pForm, setPForm] = useState({ name: "", phone: "" });

  useEffect(() => {
    if (!getCustomerToken()) {
      nav("/account/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [m, b, e, r, rf] = await Promise.all([
          customerApi.get(`/me`),
          customerApi.get(`/bookings`),
          customerApi.get(`/enquiries`),
          customerApi.get(`/rewards`).catch(() => ({ data: null })),
          customerApi.get(`/referral`).catch(() => ({ data: null })),
        ]);
        setMe(m.data);
        setPForm({ name: m.data.name || "", phone: m.data.phone || "" });
        setBookings(b.data || []);
        setEnquiries(e.data || []);
        setRewards(r.data);
        setReferral(rf.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          clearCustomerToken();
          nav("/account/login", { replace: true });
        } else {
          toast.error("Could not load your account.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const logout = () => {
    clearCustomerToken();
    nav("/", { replace: true });
  };

  const cancelBooking = async (b) => {
    const reason = window.prompt(
      `Are you sure you want to cancel ${b.unit_name || b.unit_id} (${b.start_date} → ${b.end_date})?\n\nOptional reason:`
    );
    if (reason === null) return;
    try {
      const { data } = await customerApi.post(`/bookings/${b.id}/cancel`, { reason });
      toast.success(data.message || "Booking cancelled.");
      setBookings((bs) =>
        bs.map((x) => (x.id === b.id ? { ...x, payment_status: "cancelled" } : x))
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not cancel.");
    }
  };

  const saveBooking = async (b, form) => {
    try {
      const { data } = await customerApi.post(`/bookings/${b.id}/modify`, form);
      toast.success(data.message || "Booking updated.");
      setBookings((bs) =>
        bs.map((x) =>
          x.id === b.id
            ? { ...x, ...form, total: data.total, nights: data.nights || x.nights }
            : x
        )
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not modify.");
    }
  };

  const saveProfile = async () => {
    try {
      const { data } = await customerApi.put(`/me`, pForm);
      setMe(data);
      setProfileEdit(false);
      toast.success("Profile updated.");
    } catch {
      toast.error("Could not update profile.");
    }
  };

  const upcoming = useMemo(
    () => bookings.filter((b) => b.stay_status === "upcoming" || b.stay_status === "in_progress"),
    [bookings]
  );
  const past = useMemo(() => bookings.filter((b) => b.stay_status === "past"), [bookings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] grid place-items-center">
        <Loader2 size={22} className="animate-spin text-[#B76E79]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE6]">
      <header className="bg-[#0F0E0E] text-[#F5EFE6]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="text-xs uppercase tracking-[0.32em] text-[#B76E79] font-brand">
            Walnut Lakes
          </Link>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
            <span className="hidden md:inline text-[#F5EFE6]/70">Hello, {me?.name || me?.email}</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 hover:text-[#B76E79] transition-colors"
              data-testid="customer-logout"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 space-y-10" data-testid="customer-account">
        {/* Profile */}
        <section className="bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="overline text-[#6A5F58]">
                <span className="marker-line" /> Your account
              </p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#0F0E0E] mt-2">
                {me?.name || me?.email}
              </h2>
              <p className="text-xs text-[#6A5F58] mt-1">{me?.email}</p>
            </div>
            {!profileEdit ? (
              <button
                onClick={() => setProfileEdit(true)}
                className="btn-ghost text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5"
                data-testid="profile-edit-toggle"
              >
                <Edit3 size={13} /> Edit details
              </button>
            ) : null}
          </div>
          {profileEdit && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Name</Label>
                <Input
                  value={pForm.name}
                  onChange={(e) => setPForm({ ...pForm, name: e.target.value })}
                  className="mt-1 h-10 bg-[#F5EFE6]"
                  data-testid="profile-name"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Phone</Label>
                <Input
                  value={pForm.phone}
                  onChange={(e) => setPForm({ ...pForm, phone: e.target.value })}
                  className="mt-1 h-10 bg-[#F5EFE6]"
                  data-testid="profile-phone"
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setProfileEdit(false);
                    setPForm({ name: me?.name || "", phone: me?.phone || "" });
                  }}
                  className="btn-ghost text-xs font-brand tracking-[0.14em]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveProfile}
                  className="btn-primary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5"
                  data-testid="profile-save"
                >
                  <Save size={13} /> Save
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Upcoming bookings */}
        <section>
          <p className="overline text-[#6A5F58]">
            <span className="marker-line" /> Upcoming stays
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F0E0E] mt-2">
            {upcoming.length === 0 ? "Nothing upcoming — book your next escape" : "Your next stays"}
          </h2>
          {upcoming.length === 0 ? (
            <div className="mt-4">
              <Link
                to="/#book"
                className="btn-primary text-xs font-brand tracking-[0.14em] inline-block"
              >
                Book a stay
              </Link>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {upcoming.map((b) => (
                <BookingCard key={b.id} b={b} onCancel={cancelBooking} onSave={saveBooking} />
              ))}
            </div>
          )}
        </section>

        {/* Past bookings */}
        {past.length > 0 && (
          <section>
            <p className="overline text-[#6A5F58]">
              <span className="marker-line" /> Past stays
            </p>
            <div className="mt-6 space-y-4 opacity-90">
              {past.map((b) => (
                <BookingCard key={b.id} b={b} onCancel={() => {}} onSave={() => {}} />
              ))}
            </div>
          </section>
        )}

        {/* Rewards — loyalty free-night program */}
        {rewards && (
          <RewardsPanel rewards={rewards} />
        )}

        {/* Refer a friend */}
        {referral && (
          <ReferralPanel referral={referral} />
        )}

        {/* Enquiries */}
        {enquiries.length > 0 && (
          <section>
            <p className="overline text-[#6A5F58]">
              <span className="marker-line" /> Your enquiries
            </p>
            <div className="mt-6 space-y-3">
              {enquiries.map((e) => (
                <div
                  key={e.id || e.created_at}
                  className="bg-white border border-[#DDD1BE] rounded-xl p-4 flex items-start gap-3"
                  data-testid={`enquiry-${e.id}`}
                >
                  <MessageSquare size={16} className="text-[#B76E79] mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">
                      {new Date(e.created_at).toLocaleString("en-GB")}
                      {e.interest ? ` · ${e.interest}` : ""}
                      {e.dates ? ` · ${e.dates}` : ""}
                    </div>
                    {e.message && (
                      <p className="mt-1 text-sm text-[#3A322E] whitespace-pre-wrap">{e.message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default CustomerAccount;
